import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

export interface LookupFieldConfig {
  name: string;
  label: string;
  type: 'text' | 'toggle' | 'number' | 'textarea';
  required?: boolean;
}

@Component({
  selector: 'app-create-lookup-dialog',
  templateUrl: './create-lookup-dialog.component.html',
  styleUrls: ['./create-lookup-dialog.component.scss']
})
export class CreateLookupDialogComponent implements OnInit {
  form!: FormGroup;
  isEditMode = false;
  itemId: any = null;
  fields: LookupFieldConfig[] = [];

  private readonly fieldConfigs: Record<string, LookupFieldConfig[]> = {
    'currencies': [
      { name: 'currencyId', label: 'Currency ID', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'text', required: true },
      { name: 'isDefaultValue', label: 'Default', type: 'toggle' }
    ],
    'methods-of-payment': [
      { name: 'methodOfPaymentId', label: 'Code', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'text', required: true },
      { name: 'isDefaultValue', label: 'Default', type: 'toggle' }
    ],
    'terms-of-payment': [
      { name: 'termsOfPaymentId', label: 'Code', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'text', required: true },
      { name: 'isDefaultValue', label: 'Default', type: 'toggle' }
    ],
    'departments': [
      { name: 'departmentId', label: 'Department ID', type: 'text', required: true },
      { name: 'departmentName', label: 'Department Name', type: 'text', required: true },
      { name: 'isDefaultValue', label: 'Default', type: 'toggle' }
    ],
    'banks': [
      { name: 'bankId', label: 'Bank ID', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'text', required: true },
      { name: 'isDefaultValue', label: 'Default', type: 'toggle' }
    ],
    'documents-master': [
      { name: 'documentName', label: 'Document Name', type: 'text', required: true },
      { name: 'section', label: 'Section', type: 'text' },
      { name: 'processName', label: 'Process Name', type: 'text' },
      { name: 'parmDocumentFor', label: 'Document For', type: 'text' },
      { name: 'documentType', label: 'Document Type', type: 'text' },
      { name: 'mandatory', label: 'Mandatory', type: 'toggle' },
      { name: 'fieldHint', label: 'Field Hint', type: 'textarea' },
      { name: 'expirationDate', label: 'Show Expiration Date', type: 'toggle' },
      { name: 'expirationDateMandatory', label: 'Expiration Date Required', type: 'toggle' },
      { name: 'allowMultipleDocs', label: 'Allow Multiple Documents', type: 'toggle' },
      { name: 'checkBoxRequired', label: 'Checkbox Required', type: 'toggle' },
      { name: 'additionalDetailsRequired', label: 'Additional Details Required', type: 'toggle' },
      { name: 'position', label: 'Position', type: 'number' }
    ],
    'category-tree': [
      { name: 'code', label: 'Code', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'text', required: true }
    ]
  };

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreateLookupDialogComponent>,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private toasterService: ToasterService,
    @Inject(MAT_DIALOG_DATA) public data: { lookupType: string; item: any | null }
  ) {}

  ngOnInit(): void {
    this.fields = this.fieldConfigs[this.data.lookupType] || [];
    this.isEditMode = !!this.data.item;
    this.itemId = this.data.item?.id || null;
    this.buildForm();

    if (this.isEditMode && this.data.item) {
      this.patchFormValues(this.data.item);
      this.disableIdentifierFieldsForEdit();
    }
  }

  private buildForm(): void {
    const controls: Record<string, any> = {};
    for (const field of this.fields) {
      if (field.type === 'toggle') {
        controls[field.name] = [false];
      } else if (field.type === 'number') {
        controls[field.name] = [0];
      } else {
        controls[field.name] = ['', field.required ? Validators.required : []];
      }
    }
    this.form = this.fb.group(controls);
  }

  private patchFormValues(item: any): void {
    for (const field of this.fields) {
      const rawKey = this.findMatchingItemKey(item, field.name);
      if (rawKey !== undefined) {
        const val = item[rawKey];
        if (field.type === 'toggle') {
          this.form.get(field.name)?.setValue(val === true || val === 'true');
        } else {
          this.form.get(field.name)?.setValue(val ?? '');
        }
      }
    }
  }

  private disableIdentifierFieldsForEdit(): void {
    for (const field of this.fields) {
      if (this.isIdentifierField(field.name)) {
        this.form.get(field.name)?.disable({ emitEvent: false });
      }
    }
  }

  private isIdentifierField(fieldName: string): boolean {
    const identifierFields = new Set([
      'currencyId',
      'methodOfPaymentId',
      'termsOfPaymentId',
      'departmentId',
      'bankId',
      'code',
      'documentName'
    ]);

    return identifierFields.has(fieldName);
  }

  private findMatchingItemKey(item: any, fieldName: string): string | undefined {
    const candidateKeys = [
      fieldName,
      ...this.getFieldAliases(fieldName)
    ].map(key => key.toLowerCase());

    return Object.keys(item).find(key => candidateKeys.includes(key.toLowerCase()));
  }

  private getFieldAliases(fieldName: string): string[] {
    const aliases: Record<string, string[]> = {
      methodOfPaymentId: ['code'],
      termsOfPaymentId: ['code'],
      parmDocumentFor: ['documentFor'],
      isDefaultValue: ['isDefault'],
      mandatory: ['mandatory']
    };

    return aliases[fieldName] || [];
  }

  isFieldInvalid(name: string): boolean {
    const ctrl = this.form.get(name);
    return !!(ctrl?.invalid && ctrl?.touched);
  }

  getLookupTypeLabel(): string {
    const labels: Record<string, string> = {
      'currencies': 'Currency',
      'methods-of-payment': 'Method of Payment',
      'terms-of-payment': 'Terms of Payment',
      'departments': 'Department',
      'banks': 'Bank',
      'documents-master': 'Document',
      'category-tree': 'Category'
    };
    return labels[this.data.lookupType] || 'Record';
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toasterService.showError('Please fill in all required fields', 'Validation Error');
      return;
    }

    const payload = this.form.getRawValue();

    this._loaderService.showLoader();
    try {
      let response: any;

      if (this.isEditMode) {
        const updatePayload = {
          id: this.itemId,
          ...payload
        };
        const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.Lookups}/${this.data.lookupType}/${this.itemId}`;
        response = await lastValueFrom(this.apiService.putByUrl(url, updatePayload));
      } else {
        const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.Lookups}/${this.data.lookupType}`;
        response = await lastValueFrom(this.apiService.postByUrl(url, payload));
      }

      if (response && (response.isSuccess || response.IsSuccess || response.data !== undefined || response.id || response.ID)) {
        const action = this.isEditMode ? 'updated' : 'created';
        this.toasterService.showSuccess(`${this.getLookupTypeLabel()} ${action} successfully`, 'Success');
        this.dialogRef.close(true);
      } else {
        const errorMsg = response?.errorMessageEn || response?.ErrorMessageEn || response?.message || `Failed to ${this.isEditMode ? 'update' : 'create'} record`;
        this.toasterService.showError(errorMsg, 'Error');
      }
    } catch (error: any) {
      const errorMsg = error?.error?.errorMessageEn || error?.error?.message || error?.message || 'An unexpected error occurred';
      this.toasterService.showError(errorMsg, 'Error');
      console.error('[Lookup Dialog] Submit error:', error);
    } finally {
      this._loaderService.hideLoader();
    }
  }
}
